/**
 * Workflows Tab for Admin Client Detail Page
 * Manages workflow templates, steps, rule sets, and rules.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { TextArea } from "@/components/base/textarea/textarea";
import { Toggle } from "@/components/base/toggle/toggle";
import { useAdminEnums } from "@/hooks/use-admin-enums";
import { useAdminApi } from "@/services/api/admin-api";
import type {
    CreateRuleRequestDto,
    CreateRuleSetRequestDto,
    CreateWorkflowStepRequestDto,
    CreateWorkflowTemplateRequestDto,
    DocumentTypeAdminListItemDto,
    RuleAdminListItemDto,
    RuleSetAdminDetailDto,
    StepTypeAdminListItemDto,
    UpdateRuleRequestDto,
    UpdateRuleSetRequestDto,
    UpdateWorkflowStepRequestDto,
    UpdateWorkflowTemplateRequestDto,
    WorkflowStepAdminListItemDto,
    WorkflowTemplateAdminDetailDto,
    WorkflowTemplateAdminListItemDto,
} from "@/types/api/admin";
import { RuleExpressionBuilder } from "./RuleExpressionBuilder";

// =============================================================================
// TYPES
// =============================================================================

interface WorkflowsTabProps {
    clientId: number;
}

// =============================================================================
// WORKFLOWS TAB (Main Export)
// =============================================================================

export function WorkflowsTab({ clientId }: WorkflowsTabProps) {
    const adminApi = useAdminApi();
    const [stepTypes, setStepTypes] = useState<StepTypeAdminListItemDto[]>([]);
    const [documentTypes, setDocumentTypes] = useState<DocumentTypeAdminListItemDto[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [refLoading, setRefLoading] = useState(true);

    // Load reference data on mount
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [st, dt] = await Promise.all([adminApi.listStepTypes(), adminApi.listDocumentTypes()]);
                if (!cancelled) {
                    setStepTypes(st);
                    setDocumentTypes(dt);
                }
            } catch {
                // Reference data load failure is non-critical
            } finally {
                if (!cancelled) setRefLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [adminApi]);

    if (refLoading) {
        return <LoadingIndicator size="sm" label="Loading workflow data..." />;
    }

    return (
        <div className="space-y-6">
            <WorkflowTemplateListSection
                clientId={clientId}
                documentTypes={documentTypes}
                selectedTemplateId={selectedTemplateId}
                onSelect={setSelectedTemplateId}
            />

            {selectedTemplateId && (
                <WorkflowTemplateDetailSection
                    clientId={clientId}
                    templateId={selectedTemplateId}
                    stepTypes={stepTypes}
                    documentTypes={documentTypes}
                    onDeleted={() => setSelectedTemplateId(null)}
                />
            )}
        </div>
    );
}

// =============================================================================
// WORKFLOW TEMPLATE LIST
// =============================================================================

function WorkflowTemplateListSection({
    clientId,
    documentTypes,
    selectedTemplateId,
    onSelect,
}: {
    clientId: number;
    documentTypes: DocumentTypeAdminListItemDto[];
    selectedTemplateId: number | null;
    onSelect: (id: number | null) => void;
}) {
    const adminApi = useAdminApi();
    const [templates, setTemplates] = useState<WorkflowTemplateAdminListItemDto[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Create form
    const [showCreate, setShowCreate] = useState(false);
    const [createName, setCreateName] = useState("");
    const [createDocTypeId, setCreateDocTypeId] = useState<string>("");
    const [isCreating, setIsCreating] = useState(false);

    const docTypeOptions: SelectItemType[] = useMemo(
        () => documentTypes.map((dt) => ({ id: String(dt.id), label: `${dt.name} (${dt.code})` })),
        [documentTypes],
    );

    const fetchTemplates = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await adminApi.listWorkflowTemplates(clientId, 100);
            setTemplates(res.data);
            setTotal(res.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load workflows.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId]);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const handleCreate = useCallback(async () => {
        if (!createName.trim() || !createDocTypeId) return;
        setIsCreating(true);
        try {
            const dto: CreateWorkflowTemplateRequestDto = {
                documentTypeId: Number(createDocTypeId),
                name: createName.trim(),
            };
            const created = await adminApi.createWorkflowTemplate(clientId, dto);
            setShowCreate(false);
            setCreateName("");
            setCreateDocTypeId("");
            await fetchTemplates();
            onSelect(created.id);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to create workflow.");
        } finally {
            setIsCreating(false);
        }
    }, [adminApi, clientId, createName, createDocTypeId, fetchTemplates, onSelect]);

    return (
        <section>
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-primary">Workflow Templates</h3>
                    <p className="mt-1 text-sm text-tertiary">
                        {total} workflow{total !== 1 ? "s" : ""} configured for this client.
                    </p>
                </div>
                <Button color="primary" size="sm" onClick={() => setShowCreate(!showCreate)}>
                    {showCreate ? "Cancel" : "Create Workflow"}
                </Button>
            </div>

            {/* Create form */}
            {showCreate && (
                <div className="mt-4 rounded-lg border border-secondary p-4">
                    <div className="grid max-w-xl grid-cols-1 gap-4">
                        <Select
                            label="Document Type"
                            items={docTypeOptions}
                            selectedKey={createDocTypeId}
                            onSelectionChange={(key) => setCreateDocTypeId(String(key))}
                            placeholder="Select document type..."
                            size="sm"
                        >
                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                        </Select>
                        <Input label="Name" value={createName} onChange={setCreateName} size="sm" placeholder="e.g. Invoice Processing" />
                    </div>
                    <div className="mt-4">
                        <Button color="primary" size="sm" onClick={handleCreate} isDisabled={isCreating || !createName.trim() || !createDocTypeId}>
                            {isCreating ? "Creating..." : "Create"}
                        </Button>
                    </div>
                </div>
            )}

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}

            {isLoading ? (
                <div className="mt-4">
                    <LoadingIndicator size="sm" label="Loading workflows..." />
                </div>
            ) : templates.length === 0 ? (
                <p className="mt-4 py-8 text-center text-sm text-quaternary">No workflow templates configured.</p>
            ) : (
                <div className="mt-4 overflow-x-auto rounded-lg border border-secondary">
                    <table className="w-full text-left text-sm">
                        <thead className="border-b border-secondary bg-secondary_subtle">
                            <tr>
                                <th className="px-4 py-3 font-medium text-secondary">Document Type</th>
                                <th className="px-4 py-3 font-medium text-secondary">Name</th>
                                <th className="px-4 py-3 font-medium text-secondary">Steps</th>
                                <th className="px-4 py-3 font-medium text-secondary">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary">
                            {templates.map((t) => (
                                <tr
                                    key={t.id}
                                    className={`cursor-pointer ${selectedTemplateId === t.id ? "bg-primary_hover" : "hover:bg-primary_hover"}`}
                                    onClick={() => onSelect(t.id)}
                                >
                                    <td className="px-4 py-3">
                                        <Badge color="brand" size="sm" type="pill-color">
                                            {t.documentTypeName}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3 font-medium text-primary">{t.name}</td>
                                    <td className="px-4 py-3 text-tertiary">{t.stepCount}</td>
                                    <td className="px-4 py-3">
                                        <Badge color={t.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                            {t.isActive ? "Active" : "Inactive"}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

// =============================================================================
// WORKFLOW TEMPLATE DETAIL
// =============================================================================

function WorkflowTemplateDetailSection({
    clientId,
    templateId,
    stepTypes,
    documentTypes,
    onDeleted,
}: {
    clientId: number;
    templateId: number;
    stepTypes: StepTypeAdminListItemDto[];
    documentTypes: DocumentTypeAdminListItemDto[];
    onDeleted: () => void;
}) {
    const adminApi = useAdminApi();
    const [template, setTemplate] = useState<WorkflowTemplateAdminDetailDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Edit state
    const [editName, setEditName] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editIsActive, setEditIsActive] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const fetchTemplate = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const t = await adminApi.getWorkflowTemplate(clientId, templateId);
            setTemplate(t);
            setEditName(t.name);
            setEditDescription(t.description ?? "");
            setEditIsActive(t.isActive);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load workflow.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId, templateId]);

    useEffect(() => {
        fetchTemplate();
    }, [fetchTemplate]);

    const handleSave = useCallback(async () => {
        if (!editName.trim()) return;
        setIsSaving(true);
        setError(null);
        try {
            const dto: UpdateWorkflowTemplateRequestDto = {
                name: editName.trim(),
                description: editDescription.trim() || null,
                isActive: editIsActive,
            };
            const updated = await adminApi.updateWorkflowTemplate(clientId, templateId, dto);
            setTemplate(updated);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save workflow.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, templateId, editName, editDescription, editIsActive]);

    const handleDelete = useCallback(async () => {
        setIsDeleting(true);
        setError(null);
        try {
            await adminApi.deleteWorkflowTemplate(clientId, templateId);
            onDeleted();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to delete workflow.");
            setIsDeleting(false);
        }
    }, [adminApi, clientId, templateId, onDeleted]);

    if (isLoading) {
        return (
            <section>
                <LoadingIndicator size="sm" label="Loading workflow detail..." />
            </section>
        );
    }

    if (!template) {
        return error ? <p className="text-sm text-error-primary">{error}</p> : null;
    }

    return (
        <section className="rounded-lg border border-secondary p-6">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-primary">
                        {template.name}
                        <Badge color="brand" size="sm" type="pill-color" className="ml-2">
                            {template.documentTypeName}
                        </Badge>
                    </h3>
                    {template.description && <p className="mt-1 text-sm text-tertiary">{template.description}</p>}
                </div>
                <div className="flex items-center gap-2">
                    {!confirmDelete ? (
                        <Button color="primary-destructive" size="sm" onClick={() => setConfirmDelete(true)}>
                            Delete
                        </Button>
                    ) : (
                        <>
                            <Button color="primary-destructive" size="sm" onClick={handleDelete} isDisabled={isDeleting}>
                                {isDeleting ? "Deleting..." : "Confirm Delete"}
                            </Button>
                            <Button color="secondary" size="sm" onClick={() => setConfirmDelete(false)}>
                                Cancel
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Edit fields */}
            <div className="mt-4 grid max-w-xl grid-cols-1 gap-4">
                <Input label="Name" value={editName} onChange={setEditName} size="sm" />
                <Input label="Description" value={editDescription} onChange={setEditDescription} size="sm" placeholder="Optional description" />
                <Toggle label="Active" isSelected={editIsActive} onChange={setEditIsActive} size="sm" />
            </div>

            <div className="mt-4">
                <Button color="primary" size="sm" onClick={handleSave} isDisabled={isSaving || !editName.trim()}>
                    {isSaving ? "Saving..." : "Save Changes"}
                </Button>
            </div>

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}

            {/* Steps */}
            <div className="mt-6 border-t border-secondary pt-6">
                <StepListSection clientId={clientId} templateId={templateId} steps={template.steps} stepTypes={stepTypes} onStepsChanged={fetchTemplate} />
            </div>
        </section>
    );
}

// =============================================================================
// STEP LIST
// =============================================================================

function StepListSection({
    clientId,
    templateId,
    steps,
    stepTypes,
    onStepsChanged,
}: {
    clientId: number;
    templateId: number;
    steps: WorkflowStepAdminListItemDto[];
    stepTypes: StepTypeAdminListItemDto[];
    onStepsChanged: () => void;
}) {
    const adminApi = useAdminApi();
    const [error, setError] = useState<string | null>(null);
    const [expandedStepId, setExpandedStepId] = useState<number | null>(null);

    // Create form
    const [showCreate, setShowCreate] = useState(false);
    const [createStepTypeId, setCreateStepTypeId] = useState<string>("");
    const [isCreating, setIsCreating] = useState(false);

    const stepTypeOptions: SelectItemType[] = useMemo(
        () => stepTypes.map((st) => ({ id: String(st.id), label: `${st.name} (${st.operationType})` })),
        [stepTypes],
    );

    const handleCreate = useCallback(async () => {
        if (!createStepTypeId) return;
        setIsCreating(true);
        setError(null);
        try {
            const dto: CreateWorkflowStepRequestDto = { stepTypeId: Number(createStepTypeId) };
            await adminApi.createWorkflowStep(clientId, templateId, dto);
            setShowCreate(false);
            setCreateStepTypeId("");
            onStepsChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to add step.");
        } finally {
            setIsCreating(false);
        }
    }, [adminApi, clientId, templateId, createStepTypeId, onStepsChanged]);

    const handleMoveStep = useCallback(
        async (direction: "up" | "down", stepIndex: number) => {
            const ordered = [...steps].sort((a, b) => a.stepOrder - b.stepOrder);
            const swapIndex = direction === "up" ? stepIndex - 1 : stepIndex + 1;
            if (swapIndex < 0 || swapIndex >= ordered.length) return;

            // Swap
            const newOrder = ordered.map((s) => s.id);
            [newOrder[stepIndex], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[stepIndex]];

            setError(null);
            try {
                await adminApi.reorderWorkflowSteps(clientId, templateId, { stepIds: newOrder });
                onStepsChanged();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to reorder steps.");
            }
        },
        [adminApi, clientId, templateId, steps, onStepsChanged],
    );

    const handleDeleteStep = useCallback(
        async (stepId: number) => {
            setError(null);
            try {
                await adminApi.deleteWorkflowStep(clientId, templateId, stepId);
                if (expandedStepId === stepId) setExpandedStepId(null);
                onStepsChanged();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to delete step.");
            }
        },
        [adminApi, clientId, templateId, expandedStepId, onStepsChanged],
    );

    const orderedSteps = useMemo(() => [...steps].sort((a, b) => a.stepOrder - b.stepOrder), [steps]);

    return (
        <div>
            <div className="flex items-center justify-between">
                <h4 className="font-semibold text-primary">Steps</h4>
                <Button color="secondary" size="sm" onClick={() => setShowCreate(!showCreate)}>
                    {showCreate ? "Cancel" : "Add Step"}
                </Button>
            </div>

            {/* Step chain visualization */}
            {orderedSteps.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1 text-xs">
                    {orderedSteps.map((s, i) => (
                        <span key={s.id} className="flex items-center gap-1">
                            <Badge color="gray" size="sm" type="pill-color">
                                {s.stepOrder}. {s.stepTypeName}
                            </Badge>
                            {i < orderedSteps.length - 1 && <span className="text-quaternary">&rarr;</span>}
                        </span>
                    ))}
                </div>
            )}

            {/* Create form */}
            {showCreate && (
                <div className="mt-3 rounded-lg border border-secondary p-4">
                    <div className="grid max-w-sm grid-cols-1 gap-3">
                        <Select
                            label="Step Type"
                            items={stepTypeOptions}
                            selectedKey={createStepTypeId}
                            onSelectionChange={(key) => setCreateStepTypeId(String(key))}
                            placeholder="Select step type..."
                            size="sm"
                        >
                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                        </Select>
                    </div>
                    <div className="mt-3">
                        <Button color="primary" size="sm" onClick={handleCreate} isDisabled={isCreating || !createStepTypeId}>
                            {isCreating ? "Adding..." : "Add Step"}
                        </Button>
                    </div>
                </div>
            )}

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}

            {/* Step list */}
            {orderedSteps.length === 0 ? (
                <p className="mt-4 py-4 text-center text-sm text-quaternary">No steps configured. Add a step to get started.</p>
            ) : (
                <div className="mt-4 space-y-2">
                    {orderedSteps.map((step, i) => (
                        <div key={step.id} className="rounded-lg border border-secondary">
                            {/* Step header row */}
                            <div className="flex items-center gap-3 px-4 py-3">
                                {/* Reorder buttons */}
                                <div className="flex flex-col gap-0.5">
                                    <button
                                        type="button"
                                        className="rounded px-1 text-xs text-tertiary hover:bg-secondary_subtle disabled:opacity-30"
                                        disabled={i === 0}
                                        onClick={() => handleMoveStep("up", i)}
                                    >
                                        &#9650;
                                    </button>
                                    <button
                                        type="button"
                                        className="rounded px-1 text-xs text-tertiary hover:bg-secondary_subtle disabled:opacity-30"
                                        disabled={i === orderedSteps.length - 1}
                                        onClick={() => handleMoveStep("down", i)}
                                    >
                                        &#9660;
                                    </button>
                                </div>

                                {/* Order badge */}
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-disabled_subtle text-xs font-medium text-secondary">
                                    {step.stepOrder}
                                </span>

                                {/* Step info */}
                                <div className="flex-1">
                                    <span className="font-medium text-primary">{step.stepTypeName}</span>
                                    <Badge color="gray" size="sm" type="pill-color" className="ml-2">
                                        {step.operationType}
                                    </Badge>
                                    {step.ruleSetCount > 0 && (
                                        <span className="ml-2 text-xs text-tertiary">
                                            {step.ruleSetCount} rule set{step.ruleSetCount !== 1 ? "s" : ""}
                                        </span>
                                    )}
                                </div>

                                {/* Timeout / retries */}
                                {(step.timeoutSeconds || step.maxRetries) && (
                                    <span className="text-xs text-quaternary">
                                        {step.timeoutSeconds ? `${step.timeoutSeconds}s` : ""}
                                        {step.timeoutSeconds && step.maxRetries ? " / " : ""}
                                        {step.maxRetries ? `${step.maxRetries} retries` : ""}
                                    </span>
                                )}

                                {/* Expand / Delete */}
                                <Button color="tertiary" size="sm" onClick={() => setExpandedStepId(expandedStepId === step.id ? null : step.id)}>
                                    {expandedStepId === step.id ? "Collapse" : "Edit"}
                                </Button>
                                <Button color="primary-destructive" size="sm" onClick={() => handleDeleteStep(step.id)}>
                                    Delete
                                </Button>
                            </div>

                            {/* Expanded detail */}
                            {expandedStepId === step.id && (
                                <div className="border-t border-secondary px-4 py-4">
                                    <StepDetailSection
                                        clientId={clientId}
                                        templateId={templateId}
                                        step={step}
                                        allSteps={orderedSteps}
                                        onUpdated={onStepsChanged}
                                    />
                                    <div className="mt-4 border-t border-secondary pt-4">
                                        <RuleSetsSection clientId={clientId} templateId={templateId} stepId={step.id} />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// =============================================================================
// STEP DETAIL (Edit form)
// =============================================================================

function StepDetailSection({
    clientId,
    templateId,
    step,
    allSteps,
    onUpdated,
}: {
    clientId: number;
    templateId: number;
    step: WorkflowStepAdminListItemDto;
    allSteps: WorkflowStepAdminListItemDto[];
    onUpdated: () => void;
}) {
    const adminApi = useAdminApi();
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [nextStepId, setNextStepId] = useState(step.nextStepId != null ? String(step.nextStepId) : "");
    const [onErrorStepId, setOnErrorStepId] = useState(step.onErrorStepId != null ? String(step.onErrorStepId) : "");
    const [onSkipStepId, setOnSkipStepId] = useState(step.onSkipStepId != null ? String(step.onSkipStepId) : "");
    const [configOverrides, setConfigOverrides] = useState(step.configOverrides ?? "");
    const [skipCondition, setSkipCondition] = useState(step.skipCondition ?? "");
    const [timeoutSeconds, setTimeoutSeconds] = useState(step.timeoutSeconds != null ? String(step.timeoutSeconds) : "");
    const [maxRetries, setMaxRetries] = useState(step.maxRetries != null ? String(step.maxRetries) : "");

    // Navigation dropdown options (exclude self)
    const stepNavOptions: SelectItemType[] = useMemo(() => {
        const opts: SelectItemType[] = [{ id: "", label: "(none)" }];
        for (const s of allSteps) {
            if (s.id !== step.id) {
                opts.push({ id: String(s.id), label: `Step ${s.stepOrder}: ${s.stepTypeName}` });
            }
        }
        return opts;
    }, [allSteps, step.id]);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setError(null);
        try {
            // Validate JSON fields
            if (configOverrides.trim()) {
                try {
                    JSON.parse(configOverrides);
                } catch {
                    setError("Config Overrides must be valid JSON.");
                    setIsSaving(false);
                    return;
                }
            }
            if (skipCondition.trim()) {
                try {
                    JSON.parse(skipCondition);
                } catch {
                    setError("Skip Condition must be valid JSON.");
                    setIsSaving(false);
                    return;
                }
            }

            const dto: UpdateWorkflowStepRequestDto = {
                stepTypeId: step.stepTypeId,
                nextStepId: nextStepId ? Number(nextStepId) : null,
                onErrorStepId: onErrorStepId ? Number(onErrorStepId) : null,
                onSkipStepId: onSkipStepId ? Number(onSkipStepId) : null,
                configOverrides: configOverrides.trim() || null,
                skipCondition: skipCondition.trim() || null,
                timeoutSeconds: timeoutSeconds ? Number(timeoutSeconds) : null,
                maxRetries: maxRetries ? Number(maxRetries) : null,
            };
            await adminApi.updateWorkflowStep(clientId, templateId, step.id, dto);
            onUpdated();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update step.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, templateId, step, nextStepId, onErrorStepId, onSkipStepId, configOverrides, skipCondition, timeoutSeconds, maxRetries, onUpdated]);

    return (
        <div>
            <h5 className="text-sm font-semibold text-primary">Step Configuration</h5>
            <p className="mt-0.5 text-xs text-tertiary">
                Type: {step.stepTypeName} ({step.stepTypeCode}) &middot; Operation: {step.operationType}
            </p>

            <div className="mt-3 grid max-w-xl grid-cols-2 gap-3">
                <Select label="Next Step" items={stepNavOptions} selectedKey={nextStepId} onSelectionChange={(key) => setNextStepId(String(key))} size="sm">
                    {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                </Select>
                <Select
                    label="On Error Step"
                    items={stepNavOptions}
                    selectedKey={onErrorStepId}
                    onSelectionChange={(key) => setOnErrorStepId(String(key))}
                    size="sm"
                >
                    {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                </Select>
                <Select
                    label="On Skip Step"
                    items={stepNavOptions}
                    selectedKey={onSkipStepId}
                    onSelectionChange={(key) => setOnSkipStepId(String(key))}
                    size="sm"
                >
                    {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                </Select>
                <div />

                <Input label="Timeout (seconds)" value={timeoutSeconds} onChange={setTimeoutSeconds} size="sm" type="number" placeholder="e.g. 60" />
                <Input label="Max Retries" value={maxRetries} onChange={setMaxRetries} size="sm" type="number" placeholder="e.g. 3" />
            </div>

            <div className="mt-3 grid max-w-xl grid-cols-1 gap-3">
                <TextArea label="Config Overrides (JSON)" value={configOverrides} onChange={setConfigOverrides} placeholder='{"key": "value"}' rows={3} />
                <TextArea
                    label="Skip Condition (JSON)"
                    value={skipCondition}
                    onChange={setSkipCondition}
                    placeholder='{"type": "field_null", "field": "extraction.poNumber"}'
                    rows={3}
                />
            </div>

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}

            <div className="mt-3">
                <Button color="primary" size="sm" onClick={handleSave} isDisabled={isSaving}>
                    {isSaving ? "Saving..." : "Save Step"}
                </Button>
            </div>
        </div>
    );
}

// =============================================================================
// RULE SETS
// =============================================================================

function RuleSetsSection({ clientId, templateId, stepId }: { clientId: number; templateId: number; stepId: number }) {
    const adminApi = useAdminApi();
    const [ruleSets, setRuleSets] = useState<RuleSetAdminDetailDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedRuleSetId, setExpandedRuleSetId] = useState<number | null>(null);

    // Create form
    const [showCreate, setShowCreate] = useState(false);
    const [createName, setCreateName] = useState("");
    const [isCreating, setIsCreating] = useState(false);

    const fetchRuleSets = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await adminApi.listStepRuleSets(clientId, templateId, stepId);
            setRuleSets(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load rule sets.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId, templateId, stepId]);

    useEffect(() => {
        fetchRuleSets();
    }, [fetchRuleSets]);

    const handleCreate = useCallback(async () => {
        if (!createName.trim()) return;
        setIsCreating(true);
        setError(null);
        try {
            const dto: CreateRuleSetRequestDto = { name: createName.trim() };
            await adminApi.createRuleSet(clientId, templateId, stepId, dto);
            setShowCreate(false);
            setCreateName("");
            await fetchRuleSets();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to create rule set.");
        } finally {
            setIsCreating(false);
        }
    }, [adminApi, clientId, templateId, stepId, createName, fetchRuleSets]);

    const handleDeleteRuleSet = useCallback(
        async (ruleSetId: number) => {
            setError(null);
            try {
                await adminApi.deleteRuleSet(clientId, templateId, stepId, ruleSetId);
                if (expandedRuleSetId === ruleSetId) setExpandedRuleSetId(null);
                await fetchRuleSets();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to delete rule set.");
            }
        },
        [adminApi, clientId, templateId, stepId, expandedRuleSetId, fetchRuleSets],
    );

    return (
        <div>
            <div className="flex items-center justify-between">
                <h5 className="text-sm font-semibold text-primary">Rule Sets</h5>
                <Button color="secondary" size="sm" onClick={() => setShowCreate(!showCreate)}>
                    {showCreate ? "Cancel" : "Add Rule Set"}
                </Button>
            </div>

            {showCreate && (
                <div className="mt-3 rounded-lg border border-secondary p-3">
                    <Input label="Name" value={createName} onChange={setCreateName} size="sm" placeholder="e.g. Invoice Validation Rules" />
                    <div className="mt-3">
                        <Button color="primary" size="sm" onClick={handleCreate} isDisabled={isCreating || !createName.trim()}>
                            {isCreating ? "Creating..." : "Create"}
                        </Button>
                    </div>
                </div>
            )}

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}

            {isLoading ? (
                <div className="mt-3">
                    <LoadingIndicator size="sm" label="Loading rule sets..." />
                </div>
            ) : ruleSets.length === 0 ? (
                <p className="mt-3 py-2 text-center text-xs text-quaternary">No rule sets configured for this step.</p>
            ) : (
                <div className="mt-3 space-y-2">
                    {ruleSets.map((rs) => (
                        <div key={rs.id} className="rounded-lg border border-secondary">
                            <div className="flex items-center justify-between px-3 py-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-primary">{rs.name}</span>
                                    <Badge color={rs.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                        {rs.isActive ? "Active" : "Inactive"}
                                    </Badge>
                                    <span className="text-xs text-tertiary">
                                        {rs.ruleCount} rule{rs.ruleCount !== 1 ? "s" : ""}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button color="tertiary" size="sm" onClick={() => setExpandedRuleSetId(expandedRuleSetId === rs.id ? null : rs.id)}>
                                        {expandedRuleSetId === rs.id ? "Collapse" : "Edit"}
                                    </Button>
                                    <Button color="primary-destructive" size="sm" onClick={() => handleDeleteRuleSet(rs.id)}>
                                        Delete
                                    </Button>
                                </div>
                            </div>

                            {expandedRuleSetId === rs.id && (
                                <div className="border-t border-secondary px-3 py-3">
                                    <RuleSetDetailSection clientId={clientId} templateId={templateId} stepId={stepId} ruleSet={rs} onUpdated={fetchRuleSets} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// =============================================================================
// RULE SET DETAIL (Edit + Rules list)
// =============================================================================

function RuleSetDetailSection({
    clientId,
    templateId,
    stepId,
    ruleSet,
    onUpdated,
}: {
    clientId: number;
    templateId: number;
    stepId: number;
    ruleSet: RuleSetAdminDetailDto;
    onUpdated: () => void;
}) {
    const adminApi = useAdminApi();
    const { getOptions, isLoaded: enumsLoaded } = useAdminEnums();
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Edit state
    const [editName, setEditName] = useState(ruleSet.name);
    const [editDescription, setEditDescription] = useState(ruleSet.description ?? "");
    const [editIsActive, setEditIsActive] = useState(ruleSet.isActive);
    const [editEffectiveFrom, setEditEffectiveFrom] = useState(ruleSet.effectiveFrom?.split("T")[0] ?? "");
    const [editEffectiveTo, setEditEffectiveTo] = useState(ruleSet.effectiveTo?.split("T")[0] ?? "");

    const handleSave = useCallback(async () => {
        if (!editName.trim()) return;
        setIsSaving(true);
        setError(null);
        try {
            const dto: UpdateRuleSetRequestDto = {
                name: editName.trim(),
                description: editDescription.trim() || null,
                isActive: editIsActive,
                effectiveFrom: editEffectiveFrom || null,
                effectiveTo: editEffectiveTo || null,
            };
            await adminApi.updateRuleSet(clientId, templateId, stepId, ruleSet.id, dto);
            onUpdated();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update rule set.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, templateId, stepId, ruleSet.id, editName, editDescription, editIsActive, editEffectiveFrom, editEffectiveTo, onUpdated]);

    return (
        <div>
            {/* Rule set edit form */}
            <div className="grid max-w-xl grid-cols-2 gap-3">
                <Input label="Name" value={editName} onChange={setEditName} size="sm" />
                <Input label="Description" value={editDescription} onChange={setEditDescription} size="sm" placeholder="Optional" />
                <Input label="Effective From" value={editEffectiveFrom} onChange={setEditEffectiveFrom} size="sm" type="date" />
                <Input label="Effective To" value={editEffectiveTo} onChange={setEditEffectiveTo} size="sm" type="date" />
                <Toggle label="Active" isSelected={editIsActive} onChange={setEditIsActive} size="sm" />
            </div>

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}

            <div className="mt-3">
                <Button color="primary" size="sm" onClick={handleSave} isDisabled={isSaving || !editName.trim()}>
                    {isSaving ? "Saving..." : "Save Rule Set"}
                </Button>
            </div>

            {/* Rules within this rule set */}
            <div className="mt-4 border-t border-secondary pt-4">
                <RulesSection clientId={clientId} templateId={templateId} stepId={stepId} ruleSetId={ruleSet.id} rules={ruleSet.rules} onUpdated={onUpdated} />
            </div>
        </div>
    );
}

// =============================================================================
// RULES
// =============================================================================

function RulesSection({
    clientId,
    templateId,
    stepId,
    ruleSetId,
    rules,
    onUpdated,
}: {
    clientId: number;
    templateId: number;
    stepId: number;
    ruleSetId: number;
    rules: RuleAdminListItemDto[];
    onUpdated: () => void;
}) {
    const adminApi = useAdminApi();
    const { getOptions, isLoaded: enumsLoaded } = useAdminEnums();
    const [error, setError] = useState<string | null>(null);

    // Create form
    const [showCreate, setShowCreate] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [createName, setCreateName] = useState("");
    const [createRuleType, setCreateRuleType] = useState<string>("");
    const [createSeverity, setCreateSeverity] = useState<string>("");
    const [createExpression, setCreateExpression] = useState("");
    const [createMessageTemplate, setCreateMessageTemplate] = useState("");

    // Edit state
    const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
    const [editName, setEditName] = useState("");
    const [editRuleType, setEditRuleType] = useState("");
    const [editSeverity, setEditSeverity] = useState("");
    const [editExpression, setEditExpression] = useState("");
    const [editMessageTemplate, setEditMessageTemplate] = useState("");
    const [editSortOrder, setEditSortOrder] = useState("");
    const [editIsActive, setEditIsActive] = useState(true);
    const [isEditSaving, setIsEditSaving] = useState(false);

    const ruleTypeOptions: SelectItemType[] = useMemo(
        () => (enumsLoaded ? getOptions("RuleTypeEnum").map((o) => ({ id: o.label, label: o.label })) : []),
        [enumsLoaded, getOptions],
    );

    const severityOptions: SelectItemType[] = useMemo(
        () => (enumsLoaded ? getOptions("SeverityEnum").map((o) => ({ id: o.label, label: o.label })) : []),
        [enumsLoaded, getOptions],
    );

    const handleCreate = useCallback(async () => {
        if (!createName.trim() || !createRuleType || !createSeverity) return;
        setIsCreating(true);
        setError(null);
        try {
            if (createExpression.trim()) {
                try {
                    JSON.parse(createExpression);
                } catch {
                    setError("Expression must be valid JSON.");
                    setIsCreating(false);
                    return;
                }
            }
            const dto: CreateRuleRequestDto = {
                name: createName.trim(),
                ruleType: createRuleType,
                severity: createSeverity,
                expression: createExpression.trim() || null,
                messageTemplate: createMessageTemplate.trim() || null,
            };
            await adminApi.createRule(clientId, templateId, stepId, ruleSetId, dto);
            setShowCreate(false);
            setCreateName("");
            setCreateRuleType("");
            setCreateSeverity("");
            setCreateExpression("");
            setCreateMessageTemplate("");
            onUpdated();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to create rule.");
        } finally {
            setIsCreating(false);
        }
    }, [adminApi, clientId, templateId, stepId, ruleSetId, createName, createRuleType, createSeverity, createExpression, createMessageTemplate, onUpdated]);

    const startEdit = useCallback((rule: RuleAdminListItemDto) => {
        setEditingRuleId(rule.id);
        setEditName(rule.name);
        setEditRuleType(rule.ruleType);
        setEditSeverity(rule.severity);
        setEditExpression(rule.expression ?? "");
        setEditMessageTemplate(rule.messageTemplate ?? "");
        setEditSortOrder(String(rule.sortOrder));
        setEditIsActive(rule.isActive);
    }, []);

    const handleEditSave = useCallback(async () => {
        if (!editingRuleId || !editName.trim() || !editRuleType || !editSeverity) return;
        setIsEditSaving(true);
        setError(null);
        try {
            if (editExpression.trim()) {
                try {
                    JSON.parse(editExpression);
                } catch {
                    setError("Expression must be valid JSON.");
                    setIsEditSaving(false);
                    return;
                }
            }
            const dto: UpdateRuleRequestDto = {
                name: editName.trim(),
                ruleType: editRuleType,
                severity: editSeverity,
                expression: editExpression.trim() || null,
                messageTemplate: editMessageTemplate.trim() || null,
                sortOrder: editSortOrder ? Number(editSortOrder) : 0,
                isActive: editIsActive,
            };
            await adminApi.updateRule(clientId, templateId, stepId, ruleSetId, editingRuleId, dto);
            setEditingRuleId(null);
            onUpdated();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update rule.");
        } finally {
            setIsEditSaving(false);
        }
    }, [
        adminApi,
        clientId,
        templateId,
        stepId,
        ruleSetId,
        editingRuleId,
        editName,
        editRuleType,
        editSeverity,
        editExpression,
        editMessageTemplate,
        editSortOrder,
        editIsActive,
        onUpdated,
    ]);

    const handleDeleteRule = useCallback(
        async (ruleId: number) => {
            setError(null);
            try {
                await adminApi.deleteRule(clientId, templateId, stepId, ruleSetId, ruleId);
                if (editingRuleId === ruleId) setEditingRuleId(null);
                onUpdated();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to delete rule.");
            }
        },
        [adminApi, clientId, templateId, stepId, ruleSetId, editingRuleId, onUpdated],
    );

    return (
        <div>
            <div className="flex items-center justify-between">
                <h6 className="text-xs font-semibold text-primary">Rules ({rules.length})</h6>
                <Button color="secondary" size="sm" onClick={() => setShowCreate(!showCreate)}>
                    {showCreate ? "Cancel" : "Add Rule"}
                </Button>
            </div>

            {/* Create form */}
            {showCreate && (
                <div className="mt-3 rounded-lg border border-secondary p-3">
                    <div className="grid max-w-xl grid-cols-2 gap-3">
                        <Input label="Name" value={createName} onChange={setCreateName} size="sm" />
                        <Select
                            label="Rule Type"
                            items={ruleTypeOptions}
                            selectedKey={createRuleType}
                            onSelectionChange={(key) => setCreateRuleType(String(key))}
                            size="sm"
                            placeholder="Select..."
                        >
                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                        </Select>
                        <Select
                            label="Severity"
                            items={severityOptions}
                            selectedKey={createSeverity}
                            onSelectionChange={(key) => setCreateSeverity(String(key))}
                            size="sm"
                            placeholder="Select..."
                        >
                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                        </Select>
                        <Input
                            label="Message Template"
                            value={createMessageTemplate}
                            onChange={setCreateMessageTemplate}
                            size="sm"
                            placeholder="e.g. {field} is required"
                        />
                    </div>
                    <div className="mt-3 max-w-xl">
                        <RuleExpressionBuilder value={createExpression} onChange={setCreateExpression} ruleType={createRuleType} />
                    </div>
                    <div className="mt-3">
                        <Button
                            color="primary"
                            size="sm"
                            onClick={handleCreate}
                            isDisabled={isCreating || !createName.trim() || !createRuleType || !createSeverity}
                        >
                            {isCreating ? "Creating..." : "Create Rule"}
                        </Button>
                    </div>
                </div>
            )}

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}

            {/* Rules table */}
            {rules.length === 0 ? (
                <p className="mt-3 py-2 text-center text-xs text-quaternary">No rules in this set.</p>
            ) : (
                <div className="mt-3 overflow-x-auto rounded-lg border border-secondary">
                    <table className="w-full text-left text-xs">
                        <thead className="border-b border-secondary bg-secondary_subtle">
                            <tr>
                                <th className="px-3 py-2 font-medium text-secondary">Name</th>
                                <th className="px-3 py-2 font-medium text-secondary">Type</th>
                                <th className="px-3 py-2 font-medium text-secondary">Severity</th>
                                <th className="px-3 py-2 font-medium text-secondary">Order</th>
                                <th className="px-3 py-2 font-medium text-secondary">Active</th>
                                <th className="px-3 py-2 font-medium text-secondary">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary">
                            {rules.map((rule) =>
                                editingRuleId === rule.id ? (
                                    <tr key={rule.id}>
                                        <td colSpan={6} className="px-3 py-3">
                                            {/* Inline edit form */}
                                            <div className="grid max-w-xl grid-cols-2 gap-3">
                                                <Input label="Name" value={editName} onChange={setEditName} size="sm" />
                                                <Select
                                                    label="Rule Type"
                                                    items={ruleTypeOptions}
                                                    selectedKey={editRuleType}
                                                    onSelectionChange={(key) => setEditRuleType(String(key))}
                                                    size="sm"
                                                >
                                                    {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                                                </Select>
                                                <Select
                                                    label="Severity"
                                                    items={severityOptions}
                                                    selectedKey={editSeverity}
                                                    onSelectionChange={(key) => setEditSeverity(String(key))}
                                                    size="sm"
                                                >
                                                    {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                                                </Select>
                                                <Input label="Sort Order" value={editSortOrder} onChange={setEditSortOrder} size="sm" type="number" />
                                                <Input label="Message Template" value={editMessageTemplate} onChange={setEditMessageTemplate} size="sm" />
                                                <Toggle label="Active" isSelected={editIsActive} onChange={setEditIsActive} size="sm" />
                                            </div>
                                            <div className="mt-3 max-w-xl">
                                                <RuleExpressionBuilder value={editExpression} onChange={setEditExpression} ruleType={editRuleType} />
                                            </div>
                                            <div className="mt-3 flex gap-2">
                                                <Button color="primary" size="sm" onClick={handleEditSave} isDisabled={isEditSaving}>
                                                    {isEditSaving ? "Saving..." : "Save"}
                                                </Button>
                                                <Button color="secondary" size="sm" onClick={() => setEditingRuleId(null)}>
                                                    Cancel
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={rule.id} className="hover:bg-primary_hover">
                                        <td className="px-3 py-2 font-medium text-primary">{rule.name}</td>
                                        <td className="px-3 py-2">
                                            <Badge color="gray" size="sm" type="pill-color">
                                                {rule.ruleType}
                                            </Badge>
                                        </td>
                                        <td className="px-3 py-2">
                                            <Badge color={rule.severity === "Blocking" ? "error" : "warning"} size="sm" type="pill-color">
                                                {rule.severity}
                                            </Badge>
                                        </td>
                                        <td className="px-3 py-2 text-tertiary">{rule.sortOrder}</td>
                                        <td className="px-3 py-2">
                                            <Badge color={rule.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                                {rule.isActive ? "Yes" : "No"}
                                            </Badge>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex gap-1">
                                                <Button color="tertiary" size="sm" onClick={() => startEdit(rule)}>
                                                    Edit
                                                </Button>
                                                <Button color="primary-destructive" size="sm" onClick={() => handleDeleteRule(rule.id)}>
                                                    Delete
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ),
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
